import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  getCompanyClientRequest,
  updateCompanyClientRequest,
} from "../../../../store/CompanyPortal/Clients/actions";
import { buildUpdateCompanyClientPayload } from "../clientMappers";
import {
  buildFullInternationalPhone,
} from "../../TeamsPermissions/employeeMappers";

const DEFAULT_DIAL_CODE = "+966";

const getClientDialCode = (client) => client?.country?.dial_code ?? "";

const useEditClient = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();

  const lang = useSelector((state) => state.locale.lang);
  const current = useSelector((state) => state.companyClients.current);
  const loading = useSelector((state) => state.companyClients.loading);
  const updating = useSelector((state) => state.companyClients.updating);
  const { clients, shared, inputsValidations } = messages[lang];

  const clientsBreadcrumb =
    clients.listClients.listClientsBreadcrumb || clients.title;

  const fallbackClient = location.state?.clientData;
  const client =
    current && String(current.id) === String(id)
      ? current
      : fallbackClient || null;

  const dialCode = getClientDialCode(client);

  const goToClientsList = useCallback(() => {
    history.push(ROUTE_PATHS.companyClients);
  }, [history]);

  const goToViewClient = useCallback(
    (clientData) => {
      history.push({
        pathname: ROUTE_PATHS.companyViewClient.replace(":id", id),
        state: { clientData: clientData || client },
      });
    },
    [history, id, client],
  );

  useEffect(() => {
    if (!id) return undefined;

    dispatch(
      getCompanyClientRequest({
        id,
        onError: () => goToClientsList(),
      }),
    );

    return undefined;
  }, [dispatch, id, goToClientsList]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: client?.name || "",
      email: client?.email || "",
      phone: buildFullInternationalPhone(client?.phone_number, dialCode),
      phoneCountryDialCode: String(dialCode || DEFAULT_DIAL_CODE).replace(
        /\D/g,
        "",
      ) || "966",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required("nameRequired"),
      phone: Yup.string().nullable(),
    }),
    onSubmit: (values) => {
      dispatch(
        updateCompanyClientRequest({
          id,
          data: buildUpdateCompanyClientPayload(values),
          successMessage: clients.profileUpdatedSuccess,
          onSuccess: (_response, updatedClient) => {
            goToViewClient(updatedClient);
          },
        }),
      );
    },
  });

  const breadcrumbList = useMemo(
    () => [
      { path: ROUTE_PATHS.companyClients, label: clientsBreadcrumb },
      {
        path: ROUTE_PATHS.companyViewClient.replace(":id", id),
        label: client?.name || "",
      },
      {
        path: "",
        label: clients.editProfilePageTitle || clients.editProfile,
      },
    ],
    [
      clientsBreadcrumb,
      clients.editProfile,
      clients.editProfilePageTitle,
      client?.name,
      id,
    ],
  );

  const handleCancel = useCallback(() => {
    goToViewClient();
  }, [goToViewClient]);

  return {
    clients,
    shared,
    inputsValidations,
    breadcrumbList,
    client,
    loading,
    updating,
    formik,
    onCancel: handleCancel,
  };
};

export default useEditClient;
